import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, FileText, X, Download, ChevronLeft, File as FileIcon, Image as ImageIcon, 
  Search, Grid, List, Star, StarOff, Tag, Link2, 
  SortAsc, SortDesc, Filter, FolderOpen, Camera, Music, Receipt,
  FileCheck, MapPin, Megaphone, MoreHorizontal, Edit2, Trash2,
  Eye, Check, Loader2, ChevronDown, Plus, Clock, Calendar
} from 'lucide-react';
import { AddDocumentGate } from '../subscription/FeatureGates';
import { processAndUploadDocument, deleteDocumentFromStorage } from '../../hooks/useStorage';
import { useGroup } from '../../contexts/GroupContext';
import toast from 'react-hot-toast';

// Try to import Capacitor Camera - will be undefined if not available
let CapacitorCamera = null;
let CameraResultType = null;
let CameraSource = null;

try {
  const capacitorCamera = require('@capacitor/camera');
  CapacitorCamera = capacitorCamera.Camera;
  CameraResultType = capacitorCamera.CameraResultType;
  CameraSource = capacitorCamera.CameraSource;
} catch (e) {
  // Capacitor Camera not available, will use web fallback
  console.log('Capacitor Camera not available, using web fallback');
}

// Categories with icons and colors
const CATEGORIES = {
  all: { name: 'All Documents', icon: FolderOpen, color: 'gray' },
  contracts: { name: 'Contracts', icon: FileCheck, color: 'blue' },
  riders: { name: 'Riders', icon: FileText, color: 'purple' },
  stagePlots: { name: 'Stage Plots', icon: MapPin, color: 'green' },
  receipts: { name: 'Receipts', icon: Receipt, color: 'orange' },
  promo: { name: 'Promo', icon: Megaphone, color: 'pink' },
  other: { name: 'Other', icon: FileIcon, color: 'slate' }
};

const COLOR_CLASSES = {
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400',
  purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400',
  green: 'bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400',
  pink: 'bg-pink-100 text-pink-600 dark:bg-pink-900/40 dark:text-pink-400',
  slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  red: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
};

const Documents = ({ 
  documents = [],
  shows = [],
  onNavigate,
  onAddDocument,
  onUpdateDocument,
  onDeleteDocument
}) => {
  // Get current group for Storage path
  const { currentGroup } = useGroup();
  
  // Filter out "other/personal" events - only show actual shows/gigs for linking
  const actualShows = useMemo(() => {
    return shows.filter(show => show.eventType !== 'other' && show.eventType !== 'personal');
  }, [shows]);
  
  // View & UI State
  const [viewMode, setViewMode] = useState('grid');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Filter & Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [filterShow, setFilterShow] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Upload State
  const [uploadProgress, setUploadProgress] = useState({});
  const [pendingUploads, setPendingUploads] = useState([]);
  const [pendingUploadNames, setPendingUploadNames] = useState({});
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadShow, setUploadShow] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Haptic feedback
  const haptic = (type = 'light') => {
    if (navigator.vibrate) {
      const patterns = { light: 10, medium: 25, success: [10, 50, 10] };
      navigator.vibrate(patterns[type] || 10);
    }
  };

  // Check if running in Capacitor native app
  const isNativeApp = () => {
    return window.Capacitor?.isNativePlatform?.() || false;
  };

  // Convert base64 to File object
  const base64ToFile = (base64Data, fileName, mimeType = 'image/jpeg') => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return new window.File([blob], fileName, { type: mimeType, lastModified: Date.now() });
  };

  // Handle taking photo with Capacitor Camera
  const handleTakePhoto = async () => {
    setShowUploadMenu(false);
    
    if (isNativeApp() && CapacitorCamera) {
      try {
        const image = await CapacitorCamera.getPhoto({
          quality: 80, // Reduced quality to keep file smaller
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Camera
        });
        
        const fileName = `photo_${Date.now()}.${image.format || 'jpeg'}`;
        const file = base64ToFile(image.base64String, fileName, `image/${image.format || 'jpeg'}`);
        onDrop([file], []);
      } catch (error) {
        console.error('Camera error:', error);
        if (error.message !== 'User cancelled photos app') {
          toast.error(`Camera error: ${error.message || 'Unknown error'}`);
        }
      }
    } else {
      // Fallback to web input
      window.document.getElementById('camera-input').click();
    }
  };

  // Handle choosing from gallery with Capacitor Camera
  const handleChooseFromGallery = async () => {
    setShowUploadMenu(false);
    
    if (isNativeApp() && CapacitorCamera) {
      try {
        const image = await CapacitorCamera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Photos
        });
        
        const fileName = `photo_${Date.now()}.${image.format || 'jpeg'}`;
        const file = base64ToFile(image.base64String, fileName, `image/${image.format || 'jpeg'}`);
        onDrop([file], []);
      } catch (error) {
        console.error('Gallery error:', error);
        if (error.message !== 'User cancelled photos app') {
          toast.error(`Gallery error: ${error.message || 'Unknown error'}`);
        }
      }
    } else {
      // Fallback to web input
      window.document.getElementById('file-input').click();
    }
  };

  // Handle browsing files
  const handleBrowseFiles = () => {
    setShowUploadMenu(false);
    window.document.getElementById('file-input').click();
  };

  const onDrop = useCallback(async (acceptedFiles, fileRejections) => {
    // console.log('onDrop called');
    // console.log('Accepted:', acceptedFiles?.length, acceptedFiles);
    // console.log('Rejected:', fileRejections?.length, fileRejections);
    
    if (fileRejections && fileRejections.length > 0) {
      fileRejections.forEach(({ file, errors }) => {
        const errorMessages = errors.map(e => e.message).join(', ');
        console.log(`Rejected: ${file.name} (${file.type}) - ${errorMessages}`);
        toast.error(`"${file.name}" failed: ${errorMessages}`);
      });
    }
    
    if (acceptedFiles && acceptedFiles.length > 0) {
      haptic('medium');
      setPendingUploads(acceptedFiles);
      // Initialize custom names with original file names
      const initialNames = {};
      acceptedFiles.forEach((file, index) => {
        initialNames[index] = file.name;
      });
      setPendingUploadNames(initialNames);
      setShowUploadModal(true);
    }
  }, []);

  // NEW: Upload to Firebase Storage instead of base64
  const handleConfirmUpload = async () => {
    if (!currentGroup?.id) {
      toast.error('No band selected. Please select a band first.');
      return;
    }

    const tags = uploadTags.split(',').map(t => t.trim()).filter(Boolean);
    setIsUploading(true);
    
    try {
      for (let i = 0; i < pendingUploads.length; i++) {
        const file = pendingUploads[i];
        const customName = pendingUploadNames[i] || file.name;
        
        // Show progress for this file
        setUploadProgress(prev => ({ ...prev, [i]: 0 }));
        
        // Upload to Firebase Storage and get document object
        const doc = await processAndUploadDocument(
          file,
          currentGroup.id,
          {
            customName,
            category: uploadCategory,
            showId: uploadShow || null,
            tags,
            notes: uploadNotes
          },
          (progress) => {
            setUploadProgress(prev => ({ ...prev, [i]: progress }));
          }
        );
        
        // Save document metadata to Firestore (via parent handler)
        onAddDocument(doc);
        
        // Clear progress for this file
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[i];
          return updated;
        });
      }
      
      haptic('success');
      toast.success(`${pendingUploads.length} document${pendingUploads.length > 1 ? 's' : ''} uploaded!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setPendingUploads([]);
      setPendingUploadNames({});
      setShowUploadModal(false);
      setUploadCategory('other');
      setUploadShow('');
      setUploadTags('');
      setUploadNotes('');
    }
  };

  // Handle delete with Storage cleanup
  const handleDeleteWithStorage = async (docId) => {
    const doc = documents.find(d => d.id === docId);
    if (doc?.storagePath) {
      try {
        await deleteDocumentFromStorage(doc.storagePath);
      } catch (error) {
        console.error('Error deleting from Storage:', error);
        // Continue with Firestore delete even if Storage fails
      }
    }
    onDeleteDocument(docId);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 10485760 // 10MB - Storage can handle this easily now
  });

  // Filter and sort documents
  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];
    
    if (activeCategory !== 'all') {
      filtered = filtered.filter(d => d.category === activeCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d => 
        d.name.toLowerCase().includes(query) ||
        d.notes?.toLowerCase().includes(query) ||
        d.tags?.some(t => t.toLowerCase().includes(query))
      );
    }
    
    if (filterFavorites) {
      filtered = filtered.filter(d => d.favorite);
    }
    
    if (filterShow !== 'all') {
      filtered = filtered.filter(d => d.showId === filterShow);
    }
    
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'date':
        default:
          comparison = new Date(a.uploadedAt) - new Date(b.uploadedAt);
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });
    
    return filtered;
  }, [documents, activeCategory, searchQuery, filterFavorites, filterShow, sortBy, sortOrder]);

  const categoryStats = useMemo(() => {
    const stats = {};
    Object.keys(CATEGORIES).forEach(cat => {
      if (cat === 'all') {
        stats[cat] = documents.length;
      } else {
        stats[cat] = documents.filter(d => d.category === cat).length;
      }
    });
    return stats;
  }, [documents]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return ImageIcon;
    if (type === 'application/pdf') return FileText;
    if (type === 'application/msword' || type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return FileText;
    return FileIcon;
  };

  const getFileColor = (type) => {
    if (type?.startsWith('image/')) return 'purple';
    if (type === 'application/pdf') return 'red';
    if (type === 'application/msword' || type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'blue';
    return 'blue';
  };

  const canPreview = (type) => {
    return type?.startsWith('image/') || type === 'application/pdf';
  };

  const getShowName = (showId) => {
    if (!showId || !shows || shows.length === 0) return null;
    // Handle both string and number IDs
    const show = shows.find(s => String(s.id) === String(showId));
    if (!show) return null;
    // Handle different property names (title vs venue, etc.)
    const name = show.title || show.name || show.venue || 'Unnamed Show';
    const venue = show.venue || show.location || '';
    return venue && name !== venue ? `${name} @ ${venue}` : name;
  };

  const getShowDetails = (showId) => {
    if (!showId || !shows || shows.length === 0) return null;
    // Handle both string and number IDs
    const show = shows.find(s => String(s.id) === String(showId));
    return show || null;
  };

  const toggleFavorite = (doc) => {
    haptic('light');
    onUpdateDocument(doc.id, { favorite: !doc.favorite });
  };

  const handleUpdateDoc = (updates) => {
    onUpdateDocument(editingDoc.id, updates);
    setEditingDoc(null);
    haptic('success');
  };

  // Get the display URL for a document (supports both old base64 and new Storage URLs)
  const getDocumentUrl = (doc) => {
    // New format: use downloadUrl from Storage
    if (doc.downloadUrl) {
      return doc.downloadUrl;
    }
    // Old format: base64 data URL (for backward compatibility)
    if (doc.data) {
      return doc.data;
    }
    return null;
  };

  const DocumentCard = ({ doc }) => {
    const Icon = getFileIcon(doc.type);
    const category = CATEGORIES[doc.category] || CATEGORIES.other;
    const CategoryIcon = category.icon;
    const docUrl = getDocumentUrl(doc);
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700 overflow-hidden group">
        {doc.type?.startsWith('image/') && docUrl && (
          <div className="relative h-36 overflow-hidden bg-gray-100 dark:bg-gray-900">
            <img
              src={docUrl}
              alt={doc.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
          </div>
        )}
        
        {doc.type === 'application/pdf' && (
          <div className="h-36 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 flex items-center justify-center">
            <FileText size={48} className="text-red-400" />
          </div>
        )}
        
        {(doc.type === 'application/msword' || doc.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') && (
          <div className="h-36 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 flex items-center justify-center">
            <FileText size={48} className="text-blue-400" />
          </div>
        )}
        
        {!doc.type?.startsWith('image/') && doc.type !== 'application/pdf' && doc.type !== 'application/msword' && doc.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' && (
          <div className="h-36 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
            <Icon size={48} className="text-gray-400" />
          </div>
        )}
        
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${COLOR_CLASSES[category.color]}`}>
              <CategoryIcon size={12} />
              {category.name}
            </span>
            <button
              onClick={() => toggleFavorite(doc)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              {doc.favorite ? (
                <Star size={18} className="text-yellow-500 fill-yellow-500" />
              ) : (
                <StarOff size={18} className="text-gray-400" />
              )}
            </button>
          </div>
          
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate mb-1" title={doc.name}>
            {doc.name}
          </h3>
          
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
            <span>{formatFileSize(doc.size)}</span>
            <span>•</span>
            <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>
          </div>
          
          {/* Linked Show Display - Enhanced */}
          {doc.showId && (
            <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2.5 py-1.5 rounded-lg mb-2 border border-blue-200 dark:border-blue-800">
              <Calendar size={14} className="flex-shrink-0 text-blue-500" />
              <div className="min-w-0 flex-1">
                {(() => {
                  const linkedShow = getShowDetails(doc.showId);
                  if (linkedShow) {
                    const showName = linkedShow.title || linkedShow.name || linkedShow.venue || 'Show';
                    const showVenue = linkedShow.venue || linkedShow.location || '';
                    const showDate = linkedShow.date ? new Date(linkedShow.date).toLocaleDateString() : '';
                    return (
                      <div className="truncate">
                        <span className="font-medium text-sm">{showName}</span>
                        {showVenue && showName !== showVenue && (
                          <span className="text-xs text-blue-600 dark:text-blue-400"> @ {showVenue}</span>
                        )}
                        {showDate && (
                          <span className="text-xs text-blue-500 dark:text-blue-400 ml-1">({showDate})</span>
                        )}
                      </div>
                    );
                  }
                  return <span className="text-sm font-medium">Linked to show</span>;
                })()}
              </div>
            </div>
          )}
          
          {doc.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {doc.tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                  {tag}
                </span>
              ))}
              {doc.tags.length > 3 && (
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded text-xs">
                  +{doc.tags.length - 3}
                </span>
              )}
            </div>
          )}
          
          <div className="flex gap-2">
            {canPreview(doc.type) && docUrl && (
              <button
                onClick={() => setPreviewDoc(doc)}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center gap-1"
              >
                <Eye size={14} />
                View
              </button>
            )}
            {docUrl && (
              <a
                href={docUrl}
                download={doc.name}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center gap-1"
              >
                <Download size={14} />
              </a>
            )}
            <button
              onClick={() => setEditingDoc(doc)}
              className="py-2 px-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              <Edit2 size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const DocumentRow = ({ doc }) => {
    const Icon = getFileIcon(doc.type);
    const category = CATEGORIES[doc.category] || CATEGORIES.other;
    const CategoryIcon = category.icon;
    const docUrl = getDocumentUrl(doc);
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${COLOR_CLASSES[getFileColor(doc.type)]}`}>
          {doc.type?.startsWith('image/') && docUrl ? (
            <img src={docUrl} alt="" className="w-full h-full object-cover rounded-lg" />
          ) : (
            <Icon size={24} />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
              {doc.name}
            </h3>
            {doc.favorite && <Star size={14} className="text-yellow-500 fill-yellow-500 flex-shrink-0" />}
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${COLOR_CLASSES[category.color]}`}>
              <CategoryIcon size={10} />
              {category.name}
            </span>
            <span>{formatFileSize(doc.size)}</span>
            <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>
            {/* Linked Show Display - List View */}
            {doc.showId && (() => {
              const linkedShow = getShowDetails(doc.showId);
              const showName = linkedShow?.title || linkedShow?.name || linkedShow?.venue || 'Show';
              const showVenue = linkedShow?.venue || linkedShow?.location || '';
              return (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs border border-blue-200 dark:border-blue-800">
                  <Calendar size={10} />
                  {linkedShow ? `${showName}${showVenue && showName !== showVenue ? ` @ ${showVenue}` : ''}` : 'Linked to show'}
                </span>
              );
            })()}
          </div>
        </div>
        
        <div className="hidden lg:flex gap-1 flex-shrink-0">
          {doc.tags?.slice(0, 2).map((tag, i) => (
            <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
              {tag}
            </span>
          ))}
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => toggleFavorite(doc)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {doc.favorite ? (
              <Star size={18} className="text-yellow-500 fill-yellow-500" />
            ) : (
              <StarOff size={18} className="text-gray-400" />
            )}
          </button>
          {canPreview(doc.type) && docUrl && (
            <button
              onClick={() => setPreviewDoc(doc)}
              className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 rounded-lg transition-colors"
            >
              <Eye size={18} />
            </button>
          )}
          {docUrl && (
            <a
              href={docUrl}
              download={doc.name}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 rounded-lg transition-colors"
            >
              <Download size={18} />
            </a>
          )}
          <button
            onClick={() => setEditingDoc(doc)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg transition-colors"
          >
            <Edit2 size={18} />
          </button>
          <button
            onClick={() => {
              haptic('medium');
              handleDeleteWithStorage(doc.id);
            }}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg transition-colors"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 pb-24 md:pb-8">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
        <div className="md:hidden flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => onNavigate('dashboard')} 
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-700"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Documents</h1>
          <AddDocumentGate currentCount={documents.length}>
            <div className="relative">
              <button
                onClick={() => setShowUploadMenu(!showUploadMenu)}
                className="p-2 rounded-full bg-purple-600 text-white"
              >
                <Upload size={20} />
              </button>
              
              {/* Upload Menu Dropdown */}
              {showUploadMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowUploadMenu(false)}
                  />
                  <div className="absolute right-0 top-12 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 min-w-[180px]">
                    <button
                      onClick={handleTakePhoto}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Camera size={20} className="text-purple-600" />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">Take Photo</span>
                    </button>
                    <button
                      onClick={handleChooseFromGallery}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ImageIcon size={20} className="text-blue-600" />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">Choose from Gallery</span>
                    </button>
                    <button
                      onClick={handleBrowseFiles}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <FileIcon size={20} className="text-green-600" />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">Browse Files</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </AddDocumentGate>
        </div>

        <div className="hidden md:block max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <FolderOpen className="text-purple-600" />
                Documents
                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-full text-sm font-medium">
                  {documents.length} files
                </span>
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Contracts, riders, stage plots & more</p>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 shadow-sm' : ''}`}
                >
                  <Grid size={18} className={viewMode === 'grid' ? 'text-purple-600' : 'text-gray-500'} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm' : ''}`}
                >
                  <List size={18} className={viewMode === 'list' ? 'text-purple-600' : 'text-gray-500'} />
                </button>
              </div>
              
              <AddDocumentGate currentCount={documents.length}>
                <button
                  onClick={() => window.document.getElementById('file-input').click()}
                  className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
                >
                  <Upload size={18} />
                  Upload
                </button>
              </AddDocumentGate>
              
              <button
                onClick={() => onNavigate('dashboard')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents, tags, notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setFilterFavorites(!filterFavorites)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  filterFavorites 
                    ? 'bg-yellow-100 border-yellow-300 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-600 dark:text-yellow-400' 
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Star size={16} className={filterFavorites ? 'fill-yellow-500' : ''} />
                <span className="hidden sm:inline">Favorites</span>
              </button>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  showFilters 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-600 dark:text-purple-400' 
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Filter size={16} />
                <span className="hidden sm:inline">Filters</span>
                <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
          
          {showFilters && (
            <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Sort:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm"
                >
                  <option value="date">Date</option>
                  <option value="name">Name</option>
                  <option value="size">Size</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                >
                  {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Show:</span>
                <select
                  value={filterShow}
                  onChange={(e) => setFilterShow(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm max-w-[200px]"
                >
                  <option value="all">All Shows</option>
                  {actualShows.map(show => (
                    <option key={show.id} value={show.id}>
                      {show.title} - {show.venue}
                    </option>
                  ))}
                </select>
              </div>
              
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('all');
                  setFilterFavorites(false);
                  setFilterShow('all');
                  setSortBy('date');
                  setSortOrder('desc');
                }}
                className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        <div className="md:hidden px-4 pb-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      </div>

      {/* File input for gallery/files */}
      <input
        id="file-input"
        type="file"
        multiple
        accept="image/*,application/pdf,.pdf,.doc,.docx,.txt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) {
            onDrop(files, []);
          }
          e.target.value = '';
        }}
      />
      
      {/* Camera input for taking photos */}
      <input
        id="camera-input"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) {
            onDrop(files, []);
          }
          e.target.value = '';
        }}
      />

      <div className="max-w-7xl mx-auto p-4 flex gap-6">
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sticky top-32">
            <h2 className="font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <FolderOpen size={18} />
              Categories
            </h2>
            <div className="space-y-1">
              {Object.entries(CATEGORIES).map(([key, { name, icon: Icon, color }]) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    activeCategory === key
                      ? `${COLOR_CLASSES[color]} font-medium`
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon size={16} />
                    {name}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeCategory === key ? 'bg-white/50 dark:bg-black/20' : 'bg-gray-200 dark:bg-gray-700'
                  }`}>
                    {categoryStats[key]}
                  </span>
                </button>
              ))}
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3 flex items-center gap-2">
                <Clock size={14} />
                Recently Added
              </h3>
              <div className="space-y-2">
                {documents.slice(0, 3).map(doc => (
                  <button
                    key={doc.id}
                    onClick={() => canPreview(doc.type) && setPreviewDoc(doc)}
                    className="w-full text-left p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                      {doc.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(doc.uploadedAt).toLocaleDateString()}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="lg:hidden mb-4 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
            {Object.entries(CATEGORIES).map(([key, { name, icon: Icon, color }]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === key
                    ? `${COLOR_CLASSES[color]}`
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600'
                }`}
              >
                <Icon size={14} />
                {name}
                <span className="text-xs opacity-70">({categoryStats[key]})</span>
              </button>
            ))}
          </div>

          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all mb-6 ${
              isDragActive
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 scale-[1.02]'
                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 bg-white dark:bg-gray-800'
            }`}
          >
            <input {...getInputProps()} />
            <div className={`transition-transform ${isDragActive ? 'scale-110' : ''}`}>
              <Upload className={`mx-auto mb-4 ${isDragActive ? 'text-purple-500' : 'text-gray-400'}`} size={48} />
              {isDragActive ? (
                <p className="text-purple-600 dark:text-purple-400 font-semibold text-lg">Drop files here...</p>
              ) : (
                <div>
                  <p className="text-gray-700 dark:text-gray-300 mb-2 font-medium">
                    Drag & drop files here, or click to select
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Contracts, Riders, Stage Plots, Receipts, Promo Materials
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    Supported: PDF, DOCX, DOC, PNG, JPG, HEIC, GIF, WebP, TXT • Max 10MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {Object.keys(uploadProgress).length > 0 && (
            <div className="mb-6 space-y-2">
              {Object.entries(uploadProgress).map(([id, progress]) => (
                <div key={id} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 className="animate-spin text-purple-600" size={20} />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Uploading to cloud...</span>
                    <span className="text-sm font-medium text-purple-600">{progress}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-600 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {(searchQuery || activeCategory !== 'all' || filterFavorites || filterShow !== 'all') && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Showing {filteredDocuments.length} of {documents.length} documents
            </p>
          )}

          {filteredDocuments.length === 0 ? (
            <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <FileText size={64} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
                {searchQuery || filterFavorites || filterShow !== 'all' || activeCategory !== 'all'
                  ? 'No documents match your filters'
                  : 'No documents yet'
                }
              </p>
              <p className="text-gray-400 dark:text-gray-500 text-sm">
                {searchQuery || filterFavorites || filterShow !== 'all' || activeCategory !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Upload your first contract, rider, or stage plot!'
                }
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredDocuments.map(doc => (
                <DocumentCard key={doc.id} doc={doc} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocuments.map(doc => (
                <DocumentRow key={doc.id} doc={doc} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                Upload {pendingUploads.length} File{pendingUploads.length > 1 ? 's' : ''}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Add details to organize your documents
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  File{pendingUploads.length > 1 ? 's' : ''} to Upload
                </label>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {pendingUploads.map((file, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <FileIcon size={16} className="text-gray-400 flex-shrink-0" />
                      <input
                        type="text"
                        value={pendingUploadNames[i] || ''}
                        onChange={(e) => setPendingUploadNames(prev => ({
                          ...prev,
                          [i]: e.target.value
                        }))}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="File name"
                      />
                      <span className="text-gray-400 text-xs flex-shrink-0">({formatFileSize(file.size)})</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(CATEGORIES).filter(([k]) => k !== 'all').map(([key, { name, icon: Icon, color }]) => (
                    <button
                      key={key}
                      onClick={() => setUploadCategory(key)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                        uploadCategory === key
                          ? `border-purple-500 ${COLOR_CLASSES[color]}`
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="text-xs font-medium">{name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Link to Show (optional)
                </label>
                <select
                  value={uploadShow}
                  onChange={(e) => setUploadShow(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">No show</option>
                  {actualShows.map(show => (
                    <option key={show.id} value={show.id}>
                      {show.title} - {show.venue} ({new Date(show.date).toLocaleDateString()})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={uploadTags}
                  onChange={(e) => setUploadTags(e.target.value)}
                  placeholder="e.g., 2024 Tour, Important, Tax"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={uploadNotes}
                  onChange={(e) => setUploadNotes(e.target.value)}
                  placeholder="Add any notes about this document..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setPendingUploads([]);
                  setPendingUploadNames({});
                }}
                disabled={isUploading}
                className="flex-1 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={isUploading}
                className="flex-1 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isUploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingDoc && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Edit Document</h2>
              <button
                onClick={() => setEditingDoc(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <EditDocumentForm
              doc={editingDoc}
              shows={actualShows}
              onSave={handleUpdateDoc}
              onDelete={() => {
                handleDeleteWithStorage(editingDoc.id);
                setEditingDoc(null);
                haptic('medium');
              }}
              onCancel={() => setEditingDoc(null)}
            />
          </div>
        </div>
      )}

      {previewDoc && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewDoc(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800 dark:text-gray-100">{previewDoc.name}</h3>
                <p className="text-sm text-gray-500">
                  {formatFileSize(previewDoc.size)} • {new Date(previewDoc.uploadedAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {getDocumentUrl(previewDoc) && (
                  <a
                    href={getDocumentUrl(previewDoc)}
                    download={previewDoc.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download size={16} />
                    Download
                  </a>
                )}
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              {previewDoc.type?.startsWith('image/') && getDocumentUrl(previewDoc) && (
                <img
                  src={getDocumentUrl(previewDoc)}
                  alt={previewDoc.name}
                  className="w-full h-auto"
                />
              )}
              {previewDoc.type === 'application/pdf' && getDocumentUrl(previewDoc) && (
                <iframe
                  src={getDocumentUrl(previewDoc)}
                  className="w-full h-[80vh] border-0"
                  title={previewDoc.name}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EditDocumentForm = ({ doc, shows, onSave, onDelete, onCancel }) => {
  const [name, setName] = useState(doc.name);
  const [category, setCategory] = useState(doc.category || 'other');
  const [showId, setShowId] = useState(doc.showId || '');
  const [tags, setTags] = useState(doc.tags?.join(', ') || '');
  const [notes, setNotes] = useState(doc.notes || '');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleSave = () => {
    onSave({
      name,
      category,
      showId: showId || null,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      notes
    });
  };

  return (
    <div className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          File Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Category
        </label>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(CATEGORIES).filter(([k]) => k !== 'all').map(([key, { name, icon: Icon, color }]) => (
            <button
              key={key}
              onClick={() => setCategory(key)}
              className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                category === key
                  ? `border-purple-500 ${COLOR_CLASSES[color]}`
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
              }`}
            >
              <Icon size={20} />
              <span className="text-xs font-medium">{name}</span>
            </button>
          ))}
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Link to Show
        </label>
        <select
          value={showId}
          onChange={(e) => setShowId(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="">No show</option>
          {shows.map(show => (
            <option key={show.id} value={show.id}>
              {show.title} - {show.venue}
            </option>
          ))}
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tags
        </label>
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="e.g., 2024 Tour, Important, Tax"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
        />
      </div>
      
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        {confirmDelete ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-red-600 dark:text-red-400 text-center">
              Are you sure you want to delete this document?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={onDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2"
              >
                <Check size={16} />
                Save
              </button>
            </div>
            <button
              onClick={() => setConfirmDelete(true)}
              className="w-full py-2.5 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 font-medium flex items-center justify-center gap-2"
            >
              <Trash2 size={16} />
              Delete Document
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Documents;